import { useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, DownloadSimpleIcon, UploadSimpleIcon } from '@phosphor-icons/react';

import {
  adminSkillExportHref,
  deleteAdminSkillFile,
  getAdminSkillFile,
  importOverAdminSkillZip,
  putAdminSkillFile,
  updateAdminSkill,
  useAdminSkill,
} from '@/api-models/skills';
import { errorText } from '@/features/admin/format';
import { SkillEditor, type SkillEditorApi } from '@/features/settings/components/SkillEditor';
import { Button } from '@/ui/button/Button';
import { Spinner } from '@/ui/Spinner';

/** Admin → Skills → one skill. Same editor the team surface uses, writable here. */
export function AdminSkillDetailPage() {
  const { skillId } = useParams<{ skillId: string }>();
  const { skill, mutate, isLoading } = useAdminSkill(skillId ?? null);
  const replaceInput = useRef<HTMLInputElement>(null);

  const api: SkillEditorApi = useMemo(
    () => ({
      loadFile: async (path) => (await getAdminSkillFile(skill!.id, path)).content ?? '',
      saveSkillMd: async (source) => {
        await updateAdminSkill(skill!.id, { skill_md: source, enabled: skill!.enabled });
      },
      saveFile: async (path, content) => {
        await putAdminSkillFile(skill!.id, path, content);
      },
      createFile: async (path) => {
        await putAdminSkillFile(skill!.id, path, '');
      },
      deleteFile: async (path) => {
        await deleteAdminSkillFile(skill!.id, path);
      },
      refresh: async () => {
        await mutate();
      },
    }),
    [skill, mutate],
  );

  if (isLoading && !skill) {
    return (
      <div className="flex items-center gap-2 text-sm text-fg-tertiary">
        <Spinner size={4} /> Loading skill…
      </div>
    );
  }
  if (!skill) {
    return <p className="text-sm text-fg-tertiary">This skill no longer exists.</p>;
  }

  async function handleReplace(file: File) {
    if (!skill) return;
    try {
      await importOverAdminSkillZip(skill.id, file);
      await mutate();
      toast.success('Replaced from the archive');
    } catch (e) {
      toast.error(errorText(e, 'Could not import that zip'));
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/admin/skills"
        className="inline-flex w-fit items-center gap-1.5 text-xs text-fg-tertiary hover:text-fg"
      >
        <ArrowLeftIcon size={14} />
        All system skills
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-fg">{skill.name}</h1>
          <p className="mt-1 text-xs text-fg-muted">{skill.description}</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={adminSkillExportHref(skill.id)} download>
            <Button size={9} iconLeft={<DownloadSimpleIcon size={16} />}>
              Export
            </Button>
          </a>
          <input
            ref={replaceInput}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleReplace(file);
            }}
          />
          <Button size={9} onClick={() => replaceInput.current?.click()} iconLeft={<UploadSimpleIcon size={16} />}>
            Replace from zip
          </Button>
        </div>
      </div>

      <p className="rounded-lg border border-line bg-surface-2 px-4 py-3 text-xs text-warning">
        Every space sees this skill. If it lists <code>allowed-tools</code>, those tools are pre-approved for the whole
        session in every team that has it on.
      </p>

      <SkillEditor skill={skill} api={api} />
    </div>
  );
}
